/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, {useMemo} from "react";
import Box from "@mui/material/Box";
import {ErrorMessage, useField, useFormikContext} from "formik";
import DynamicSelect from "qqq/components/forms/DynamicSelect";
import {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {useRowEditContext} from "../RowEditContext";

interface Props
{
   fieldName: string;
   formFieldDef: DynamicFormFieldDefinition;
}

export const PossibleValueEditCell: React.FC<Props> = ({fieldName, formFieldDef}) =>
{
   const {values, setFieldValue} = useFormikContext<any>();
   const [, meta] = useField(fieldName);
   const showError = meta.touched && !!meta.error;
   const triggerAdjuster = useRowEditContext()?.handleFieldBlur;

   // Pass ALL current row values so the backend can resolve PV filters like
   // `${input.someOtherField}`. See RecordScreenField.tsx for the pattern.
   const otherValues = useMemo(() =>
   {
      const m = new Map<string, any>();
      for (const k in values) m.set(k, values[k]);
      return m;
   }, [values]);

   ///////////////////////////////////////////////////////////////////////////////////
   // Force DynamicSelect to fill the cell's full width.  Without the explicit      //
   // wrapper the Autocomplete's outer `.DynamicSelectAutoCompleteWrapper` Box has  //
   // no width and collapses to the Autocomplete's intrinsic width (~100px), which  //
   // ignores the bumped column min-width (160px).                                  //
   // Adds a red-outline override when formik flags a validation error on this      //
   // field (DynamicSelect doesn't expose an `error` prop like OutlinedInput does). //
   //                                                                               //
   // DynamicSelect is uncontrolled — it seeds its displayed value once at mount    //
   // from initialValue + initialDisplayValue.  When an on-change form adjuster     //
   // programmatically sets this field to a possible value, only the props change;  //
   // an uncontrolled select would ignore them.  Key on the adjuster-applied        //
   // possibleValueProps.initialValue (id) AND initialDisplayValue (label) so the   //
   // select remounts and reflects the adjuster-supplied value+label — including    //
   // the id covers a new value that shares the old one's label.  Neither prop      //
   // changes as the user types/selects, so this never remounts mid-interaction.    //
   ///////////////////////////////////////////////////////////////////////////////////
   return (
      <Box sx={{width: "100%", display: "flex", flexDirection: "column",
         "& .DynamicSelectAutoCompleteWrapper": {width: "100%"},
         ...(showError ? {"& .MuiOutlinedInput-notchedOutline": {borderColor: "error.main"}} : {}),
      }}>
         <DynamicSelect
            key={`${fieldName}-pvs-${formFieldDef.possibleValueProps?.initialValue ?? ""}-${formFieldDef.possibleValueProps?.initialDisplayValue ?? ""}`}
            fieldPossibleValueProps={formFieldDef.possibleValueProps}
            name={fieldName}
            isEditable={formFieldDef.isEditable}
            fieldLabel=""
            useCase="form"
            inForm={false}
            initialValue={values?.[fieldName]}
            otherValues={otherValues}
            onChange={(value: any) =>
            {
               const newId = value ? value.id : null;
               setFieldValue(fieldName, newId);
               triggerAdjuster?.(fieldName, newId);
            }}
         />
         {showError && (
            <Box className="qqq-row-edit-field-error" sx={{fontSize: "0.75rem", color: "error.main", mt: "2px", mb: "2px", lineHeight: 1.2, whiteSpace: "normal", wordBreak: "break-word"}}>
               <ErrorMessage name={fieldName} render={msg => <span data-field-error="true">{msg}</span>} />
            </Box>
         )}
      </Box>
   );
};
